import { Component, ContentChild, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidatorFn } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';

import { BaseInterface } from '@interfaces/base-interface';
import { ShowTagsIconsDirective } from 'app/shared/directives/show-tags-icons.directive';

@Component({
    selector: 'app-autocomplete-multiple',
    templateUrl: './autocomplete-multiple.component.html',
    styleUrls: ['./autocomplete-multiple.component.scss']
})
export class AutocompleteMultipleComponent<T extends BaseInterface> implements OnChanges {
    @ContentChild(ShowTagsIconsDirective) customTemplate: ShowTagsIconsDirective;
    @Input() list: T[];
    @Input() control: UntypedFormControl;
    @Input() operator: UntypedFormControl;
    @Input() label: string;
    @Input() required: boolean;
    @Input() showDisplays: boolean;
    @Input() showShortDisplays: boolean;
    @Input() disableCondition: (item: T) => boolean;

    @Output() openedChange = new EventEmitter<boolean>();

    filteredList: T[];
    searchControl = new UntypedFormControl();

    controlValue: T[];

    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        
        if (changes.list?.currentValue !== changes.list?.previousValue) {
            this.controlValue = this.control?.value?.length ? this.control.value : [];
            this.filteredList = this.list.slice();
            this.list.sort(this.compare)
            if (this.control) {
                this.initControls();
            }
        }
    }

    clearSearch() {
        this.searchControl.setValue('');
        this.filteredList = this.list.slice();
    }

    removeValue(valueToRemove: T): void {
        if (this.control.enabled) {
            const newValues = this.control.value.filter(v => v.id !== valueToRemove.id);
            this.control.setValue(newValues);
        }
    }

    compareFunction(item1: T, item2: T): boolean {
        return item1 && item2 ? item1.id === item2.id : item1 === item2;
    }

    private initControls(): void {
        const validators = [this.forbiddenTypeValidator('string')];
        this.control.addValidators(validators);

        this.control.valueChanges
            .subscribe((values: T[]) => {
                const valuesNotFiltered = this.controlValue.filter(cV => !this.filteredList.find(item => item.id === cV.id));
                this.controlValue = values?.length ? Array.from(new Set([...valuesNotFiltered, ...values])) : Array.from(new Set([...valuesNotFiltered]));
                this.control.setValue(this.controlValue, { emitEvent: false });
            });

        this.searchControl.valueChanges
            .pipe(
                startWith(''),
                map((value: string | T) => typeof value === 'string' ? value : value.display),
                map((value: string) => value ? this.filter(value) : this.list.slice())
            ).subscribe((list: T[]) => this.filteredList = list);

    }

    private filter(value: string): T[] {
        const filterValue = value.toLowerCase();
        return this.list.filter(item => item.doesInclude(filterValue));
    }

    private forbiddenTypeValidator(forbiddenType: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const forbidden = control.value !== '' && typeof control.value === forbiddenType;
            return forbidden ? { forbiddenType: { value: control.value } } : null;
        };
    }

    private compare(a, b) {
        if (a.display < b.display) {
            return -1;
        }
        if (a.display > b.display) {
            return 1;
        }
        return 0;
    }

}
